import { useState } from 'react';
import { InstallPWA } from './InstallPWA';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { Sheet, SheetContent } from '../components/ui/sheet';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { LayoutDashboard, Building2, MapPin, Users, Shield, Truck, Tags, Package, Cog, ClipboardList, CheckCircle, Upload, FileText, Menu, LogOut, ChevronDown, Settings, BarChart3, GitCompareArrows, TrendingDown, ShieldAlert, FileOutput, Layers, MapPinned, Calendar, Brain, BookOpen, Activity } from 'lucide-react';
import vexorLogoDark from '../assets/vexor-logo-dark.png';

const NAV_SECTIONS = [
  { label: 'OVERVIEW', items: [
    { label: 'Dashboard', path: '/', icon: LayoutDashboard },
    { label: 'Fleet Status', path: '/fleet', icon: Activity },
  ]},
  { label: 'ANALYTICS', perm: { module: 'analytics', action: 'view' }, items: [
    { label: 'Spend & Performance', path: '/analytics', icon: BarChart3 },
    { label: 'Site Analytics', path: '/analytics/sites', icon: MapPin },
    { label: 'Machine Analytics', path: '/analytics/machines', icon: Cog },
    { label: 'Tooth Point Comparison', path: '/analytics/products', icon: GitCompareArrows },
    { label: 'Supplier Comparison', path: '/analytics/suppliers', icon: Truck },
    { label: 'Strata & Type Analysis', path: '/analytics/strata', icon: Layers },
    { label: 'Savings Opportunities', path: '/analytics/savings', icon: TrendingDown },
    { label: 'AI Savings Agent', path: '/analytics/savings-agent', icon: Brain, perm: { module: 'commercial', action: 'view' } },
    { label: 'Data Quality', path: '/analytics/data-quality', icon: ShieldAlert },
  ]},
  { label: 'REPORTS', perm: { module: 'reports', action: 'view' }, items: [
    { label: 'Reports & Export', path: '/reports', icon: FileOutput },
  ]},
  { label: 'ORGANIZATION', perm: { module: 'companies', action: 'view' }, items: [
    { label: 'Companies', path: '/companies', icon: Building2 },
    { label: 'Locations', path: '/locations', icon: MapPinned },
    { label: 'Sites', path: '/sites', icon: MapPin },
    { label: 'Users', path: '/users', icon: Users, perm: { module: 'users', action: 'view' } },
    { label: 'Permissions', path: '/permissions', icon: Shield, perm: { module: 'users', action: 'edit' } },
  ]},
  { label: 'MASTERS', perm: { module: 'products', action: 'edit' }, items: [
    { label: 'Suppliers', path: '/suppliers', icon: Truck, perm: { module: 'suppliers', action: 'edit' } },
    { label: 'Categories', path: '/categories', icon: Tags, perm: { module: 'categories', action: 'edit' } },
    { label: 'Products', path: '/products', icon: Package, perm: { module: 'products', action: 'edit' } },
    { label: 'Machines', path: '/machines', icon: Cog, perm: { module: 'machines', action: 'edit' } },
  ]},
  { label: 'OPERATIONS', perm: { module: 'operations', action: 'view' }, items: [
    { label: 'New Installation / Change-Out', path: '/change-out', icon: ClipboardList, perm: { module: 'operations', action: 'create' } },
    { label: 'Close Active Cycle', path: '/close-cycle', icon: CheckCircle, perm: { module: 'operations', action: 'create' } },
    { label: 'Approvals', path: '/approvals', icon: CheckCircle, perm: { module: 'approvals', action: 'edit' } },
  ]},
  { label: 'DATA', perm: { module: 'imports', action: 'create' }, items: [
    { label: 'Excel Import', path: '/imports', icon: Upload },
  ]},
  { label: 'SYSTEM', perm: { module: 'audit', action: 'view' }, roles: ['super_admin'], items: [
    { label: 'Audit Log', path: '/audit', icon: FileText },
    { label: 'Weekly Digest', path: '/digest', icon: FileOutput },
  ]},
  { label: 'HELP', items: [
    { label: 'User Guide', path: '/onboarding', icon: BookOpen },
  ]},
];

function hasPerm(user, perm) {
  if (!perm) return true;
  if (user?.role === 'super_admin') return true;
  return user?.permissions?.[perm.module]?.[perm.action] === true;
}

function SidebarContent({ location, user }) {
  const userRole = user?.role;
  const filteredSections = NAV_SECTIONS
    .filter(section => {
      if (section.roles && !section.roles.includes(userRole)) return false;
      if (!hasPerm(user, section.perm)) return false;
      return true;
    })
    .map(section => ({
      ...section,
      items: section.items.filter(item => {
        if (item.roles && !item.roles.includes(userRole)) return false;
        if (!hasPerm(user, item.perm)) return false;
        return true;
      }),
    }))
    .filter(section => section.items.length > 0);

  return (
    <div className="flex flex-col h-full bg-slate-900">
      <div className="px-6 py-5 border-b border-slate-800">
        <h1 className="text-lg font-bold text-white tracking-tight font-heading" data-testid="app-logo">DigSense IQ</h1>
        <p className="text-xs text-slate-400 mt-0.5">Track life. Compare cost. Cut waste.</p>
      </div>
      <ScrollArea className="flex-1 px-3 py-4">
        {filteredSections.map((section) => (
          <div key={section.label} className="mb-5">
            <p className="px-3 mb-2 text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-500">{section.label}</p>
            {section.items.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path || (item.path !== '/' && location.pathname.startsWith(item.path));
              return (
                <Link key={item.path} to={item.path} data-testid={`nav-${item.label.toLowerCase().replace(/[\s&]/g, '-')}`} className={`sidebar-nav-item ${isActive ? 'active' : 'text-slate-400'}`}>
                  <Icon size={16} strokeWidth={1.5} /><span>{item.label}</span>
                </Link>
              );
            })}
          </div>
        ))}
      </ScrollArea>
      <div className="px-6 py-3 border-t border-slate-800 flex items-center gap-2" data-testid="sidebar-vendor-attribution">
        <span className="text-[10px] text-slate-500">Powered by</span>
        <img src={vexorLogoDark} alt="VEXOR GLOBAL" className="h-3.5 w-auto opacity-90" />
      </div>
    </div>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = async () => { await logout(); navigate('/login'); };
  const roleBadge = (role) => ({ super_admin: 'Super Admin', company_admin: 'Company Admin', site_admin: 'Site Admin', site_user: 'Site User', read_only: 'Viewer' }[role] || role);

  return (
    <div className="flex h-screen overflow-hidden bg-slate-50">
      <aside className="hidden lg:flex lg:w-60 xl:w-64 flex-shrink-0" data-testid="desktop-sidebar"><SidebarContent location={location} user={user} /></aside>
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}><SheetContent side="left" className="p-0 w-64"><SidebarContent location={location} user={user} /></SheetContent></Sheet>
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-14 bg-white border-b border-slate-200 flex items-center justify-between px-4 lg:px-8 flex-shrink-0" data-testid="app-header">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" className="lg:hidden" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-btn"><Menu size={20} /></Button>
          </div>
          <div className="flex items-center gap-3">
            <InstallPWA />
            <span className="hidden sm:inline text-xs font-medium text-slate-500 bg-slate-100 px-2.5 py-1 rounded-full">{roleBadge(user?.role)}</span>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="flex items-center gap-2" data-testid="user-menu-btn">
                  <div className="w-7 h-7 rounded-full bg-slate-900 flex items-center justify-center text-white text-xs font-bold">{user?.name?.charAt(0)?.toUpperCase() || 'U'}</div>
                  <span className="hidden sm:inline text-sm font-medium text-slate-700">{user?.name}</span>
                  <ChevronDown size={14} className="text-slate-400" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <div className="px-2 py-1.5"><p className="text-sm font-medium">{user?.name}</p><p className="text-xs text-slate-500">{user?.email}</p></div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="logout-menu-item"><LogOut size={14} className="mr-2" /> Sign Out</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        <main className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8 xl:p-12" data-testid="main-content"><div className="max-w-7xl mx-auto">{children}</div></main>
      </div>
    </div>
  );
}
